// FLAG:useDocuAi — the "review again" save, shared by BOTH hosts of the review component:
// the Documents review page (`document-invoice-review-page.tsx`) and the expense-embedded review
// (`receipt/review/embedded-invoice-review.tsx`). It owns three things that must never differ
// between the two: whether this save re-opens an already reviewed invoice, the read-only preview
// the confirmation dialog shows, and the reviewer's answer about a renamed payee.
//
// Without this, the expense entry silently lost one-way sync: it saved WITHOUT the
// `reReviewSave` marker, so the corrected values landed on the document and the expense kept the
// old ones — the exact defect the marker exists to prevent.
import { useCallback, useEffect, useRef, useState } from 'react'
import FileService from '@/modules/file/fileService'
import { isReReviewStatus } from '@/modules/document/utils/invoice-analysis-state'
import {
  resolveLinkedPartyDivergence,
  ReviewConfirmMode,
  ReviewSaveEffects,
} from '@/modules/document/components/review/re-review-confirm-dialog'
import { PartyResolution } from '@/modules/receipt/review/review-payee-save'
import { ReviewPayeeState } from '@/modules/receipt/review/review-payee-state'

// The statuses that mean "already reviewed" now live in invoice-analysis-state (which also gates the
// drawer's embedded review on them); re-exported here so the existing importers stay unchanged.
export { isReReviewStatus }

// FLAG:useDocuAi — T13 of plan omxweb-draft-receipt-instead-of-review-row (2026-09-17): a FIRST
// review asks the party question only when it has to. `updateReviewPayee` arms `updateDirectory`
// on the first edit of a LINKED record, and the save then renames the master record — that arming
// is exactly what turned dev supplier 97849 into "Baustoff+Metall GmbH2" without anybody being
// asked. Unlinked, or linked and untouched: no round trip, the save runs as it always did.
const needsFirstReviewPartyCheck = (reviewPayee?: ReviewPayeeState | null) =>
  !!reviewPayee?.directoryRef && reviewPayee.updateDirectory === true

// The question is meaningful only when the record the server describes IS the record the form is
// linked to. A reviewer who picked a different supplier from the directory has already answered it.
const isFormLinkedTo = (
  reviewPayee: ReviewPayeeState | null | undefined,
  linked: ReviewSaveEffects['party'] extends infer P ? (P extends { linked: infer L } ? L : never) : never,
) => !!linked && !!reviewPayee?.directoryRef
  && linked.type === reviewPayee.directoryRef.type
  && Number(linked.id) === Number(reviewPayee.directoryRef.id)

export const useReReviewSave = ({
  fileId,
  reviewStatus,
  createExpense = false,
  bookingTarget = null,
  reviewPayee,
  invalidEInvoice = false,
  onProceed,
}: {
  fileId?: string | number | null
  // Keyed on the status as LOADED, so it cannot flip mid-edit.
  reviewStatus?: string | null
  // Mirrors the "Ausgabe erstellen" checkbox — it decides whether a missing expense is announced
  // as "will be created" or as "none exists".
  createExpense?: boolean
  // FLAG:useDocuAi — mirrors the „Dieser Beleg ist eine Einnahme" checkbox while this host offers it.
  // Exactly the same purpose as createExpense above: the preview should announce the ledger the save
  // is about to affect — not the most recently persisted one. `null` = this host does not ask the
  // question, so the server reads the persisted state as before.
  bookingTarget?: string | null
  reviewPayee?: ReviewPayeeState | null
  // FLAG:useDocuAi — did the format/KoSIT validation judge this e-invoice invalid? Merely passed
  // through: the dialog then shows the warning INSIDE itself instead of `continueSave`
  // (receipt-edit.tsx) opening a second modal afterwards. A reviewer who must confirm twice in a row
  // no longer reads the second message.
  invalidEInvoice?: boolean
  onProceed: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [effects, setEffects] = useState<ReviewSaveEffects | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [partyResolution, setPartyResolution] = useState<PartyResolution | null>(null)
  const [mode, setMode] = useState<ReviewConfirmMode>('reReview')
  // The first-review preview runs with the dialog still closed, so nothing disables the Save button
  // meanwhile; a second click must not start a second preview.
  const previewInFlight = useRef(false)

  const isReReview = isReReviewStatus(reviewStatus)

  // A different document must never inherit the previous one's answer (the expense host keeps
  // this mounted across previous/next navigation).
  useEffect(() => {
    setPartyResolution(null)
    setIsOpen(false)
  }, [fileId])

  // FLAG:useDocuAi — T19: the preview must describe the record the FORM is linked to. On a first
  // review the stored snapshot has no directoryRef (build-review-data derives the link from the
  // analysis match on the client), so a preview reading only the snapshot answered `linked: null`
  // and the party question never appeared — the save then renamed the supplier silently.
  const previewParams = useCallback(() => ({
    createExpense,
    bookingTarget,
    ...(reviewPayee?.directoryRef
      ? { directoryRef: reviewPayee.directoryRef, payeeRole: reviewPayee.role }
      : {}),
  }), [createExpense, bookingTarget, reviewPayee])

  const requestSave = useCallback(async() => {
    if (!isReReview) {
      // A first-time review saves exactly as before — no dialog, no extra round trip — unless the
      // reviewer edited a LINKED record; then the preview says whether the typed name still matches
      // it, and only a divergence opens the (party-only) dialog.
      if (!needsFirstReviewPartyCheck(reviewPayee)) {
        onProceed()
        return
      }
      if (previewInFlight.current) return
      previewInFlight.current = true
      setMode('firstReview')
      setIsLoading(true)
      setHasError(false)
      try {
        const { body } = await FileService.fetchReviewSaveEffects(fileId, previewParams())
        const nextEffects: ReviewSaveEffects | null = body?.data ?? null
        const { linked, hasDivergedIdentity } = resolveLinkedPartyDivergence(nextEffects, reviewPayee)
        if (!hasDivergedIdentity || !isFormLinkedTo(reviewPayee, linked)) {
          onProceed()
          return
        }
        setEffects(nextEffects)
        setIsOpen(true)
      } catch {
        // Nobody knows now whether this save would rename a master record. Same rule as the
        // re-review: no blind save — the dialog shows the error, the user cancels and retries.
        setEffects(null)
        setHasError(true)
        setIsOpen(true)
      } finally {
        setIsLoading(false)
        previewInFlight.current = false
      }
      return
    }

    setMode('reReview')
    setIsOpen(true)
    setIsLoading(true)
    setHasError(false)
    try {
      // request() resolves { body: <response payload> }; the route answers { meta, data }.
      const { body } = await FileService.fetchReviewSaveEffects(fileId, previewParams())
      setEffects(body?.data ?? null)
    } catch {
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }, [isReReview, onProceed, fileId, reviewPayee, previewParams])

  // The answer travels to the save as a PROP (not through the redux form), so the save cannot race
  // a pending state update and write the pre-dialog snapshot.
  const confirm = useCallback((resolution: PartyResolution | null) => {
    setPartyResolution(resolution)
    setIsOpen(false)
    onProceed()
  }, [onProceed])

  return {
    isReReview,
    partyResolution,
    requestSave,
    dialogProps: {
      open: isOpen,
      effects,
      isLoading,
      hasError,
      reviewPayee,
      invalidEInvoice,
      mode,
      onCancel: () => setIsOpen(false),
      onConfirm: confirm,
    },
  }
}
